import React, { useState, useEffect, useContext, createContext, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { IoCloseOutline } from "react-icons/io5"; // Close icon
import "./PopupNotification.css";

const Notification = ({
  theme = "blue",
  headingText = "",
  message = "",
  icon = null,
  forceClose = false,
  popupTime = 5000,
  onClose,
}) => {
  useEffect(() => {
    if (!forceClose) {  // Auto-close only when forceClose is false
      const timer = setTimeout(() => {
        onClose();
      }, popupTime);
      return () => clearTimeout(timer);
    }
  }, [forceClose, onClose, popupTime]);

  return (
    <div className={`notification ${theme} show`}>
      <div className="notification-inner">
        <div className="notification-content">
          {icon && <div className="notification-icon">{icon}</div>}
          <div className="notification-text">
            {headingText && <h4 className="notification-heading">{headingText}</h4>}
            <p className="notification-message">{message}</p>
          </div>
        </div>
        <button type="button" className="close-btn" onClick={onClose} aria-label="Close notification">
          <IoCloseOutline size={20} aria-hidden />
        </button>
      </div>
    </div>
  );
};

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const removeNotification = useCallback((id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isVisible: false } : n))
    );

    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 300);
  }, []);

  const addNotification = useCallback((config) => {
    const id = Date.now();
    const newNotification = { ...config, id, isVisible: true };
    setNotifications((prev) => [...prev, newNotification]);

    const popupTime = config?.popupTime ?? 5000;
    if (popupTime > 0 && !config?.forceClose) {
      setTimeout(() => {
        removeNotification(id);
      }, popupTime);
    }

    return id;
  }, [removeNotification]);

  const contextValue = useMemo(() => ({ addNotification }), [addNotification]);

  const notificationLayer =
    typeof document !== 'undefined' && document.body && notifications.length > 0
      ? createPortal(
          <>
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`notification-container ${notification.position || "bottom-right"}`}
              >
                <Notification {...notification} onClose={() => removeNotification(notification.id)} />
              </div>
            ))}
          </>,
          document.body
        )
      : null;

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      {notificationLayer}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within a NotificationProvider");
  }
  return context;
};


// import { useNotification } from "../../layout/ReusableComponent/PopupNotification/PopupNotification";

//     const { addNotification } = useNotification();

//     const showNotification = () => {
//         addNotification({
//             headingText: "Success!",
//             message: "Your action was completed successfully. Your action was completed successfully.",
//             forceClose: true,
//             position: "bottom-right",
//         });
//     };